import React, { useState } from 'react';
import { Download, Upload, X } from 'lucide-react';
import { companyApi } from '../services/companyApi';

/**
 * 투자처 일괄 등록 — factsheet FAQ Q2 「벌크 업로드」 대조 (2026-10-08).
 *
 * 양식(CSV)을 받아 채워 올리면 미리보기에서 확인하고 한 번에 등록한다.
 * 회사명이 비었거나 이미 있는 이름이면 건너뛴다 — 중복 등록을 막는다.
 */
const HEADER = ['회사명', '업종', '사업자번호', '설립일(YYYY-MM-DD)', '홈페이지', '메모'];

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = []; let cell = ''; let q = false;
  const t = text.replace(/^﻿/, '');
  for (let i = 0; i < t.length; i++) {
    const ch = t[i];
    if (q) {
      if (ch === '"' && t[i + 1] === '"') { cell += '"'; i++; } else if (ch === '"') q = false; else cell += ch;
    } else if (ch === '"') q = true;
    else if (ch === ',') { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && t[i + 1] === '\n') i++;
      row.push(cell); rows.push(row); row = []; cell = '';
    } else cell += ch;
  }
  if (cell || row.length) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.trim()));
}

export const BulkUploadModal: React.FC<{ open: boolean; existing: string[]; onClose: () => void; onDone: () => void }> = ({ open, existing, onClose, onDone }) => {
  const [rows, setRows] = useState<string[][]>([]);
  const [busy, setBusy] = useState(false);
  // 줄마다 결과 — 한 줄이 막혀도(사업자번호 중복 등) 나머지는 들어가고, 막힌 줄과 이유를 보인다
  const [fails, setFails] = useState<string[]>([]);
  if (!open) return null;

  const status = (r: string[]) => (!r[0]?.trim() ? '회사명 없음' : existing.includes(r[0].trim()) ? '이미 있음' : '등록');
  const ok = rows.filter((r) => status(r) === '등록');

  const template = () => {
    const blob = new Blob(['﻿' + HEADER.join(',') + '\n예시기업,제조 AI,123-45-67890,2023-01-02,https://example.com,\n'], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = '투자처_일괄등록_양식.csv'; a.click();
  };
  const pick = async (f: File | undefined) => {
    if (!f) return;
    const all = parseCsv(await f.text());
    setRows(all[0]?.[0]?.includes('회사명') ? all.slice(1) : all);
  };
  const run = async () => {
    setBusy(true); setFails([]);
    const bad: string[] = [];
    try {
      for (const r of ok) {
        try {
          await companyApi.create({ name: r[0].trim(), sector: r[1] ?? '', bizNo: r[2] ?? '', foundedOn: r[3] ?? '', homepage: r[4] ?? '', note: r[5] ?? '' });
        } catch (e) { bad.push(`${r[0].trim()} — ${(e as Error).message}`); }
      }
      if (bad.length) { setFails(bad); setRows(ok.filter((r) => bad.some((b) => b.startsWith(r[0].trim() + ' —')))); }
      else { setRows([]); onDone(); }
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-2xl my-8">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900">투자처 일괄 등록</h2>
          <button onClick={() => { setRows([]); onClose(); }} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            <button onClick={template} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50"><Download size={12} /> 양식 받기</button>
            <label className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 cursor-pointer">
              <Upload size={12} /> CSV 고르기
              <input id="bulk-file" type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
            </label>
          </div>
          {rows.length > 0 && (
            <div className="border border-slate-200 rounded-lg overflow-x-auto max-h-80">
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-50 text-slate-500">{HEADER.slice(0, 4).map((h) => <th key={h} className="text-left font-medium px-3 py-1.5">{h}</th>)}<th className="text-left font-medium px-3">결과</th></tr></thead>
                <tbody>{rows.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    {[0, 1, 2, 3].map((j) => <td key={j} className="px-3 py-1.5">{r[j]}</td>)}
                    <td className={`px-3 font-medium ${status(r) === '등록' ? 'text-emerald-700' : 'text-amber-700'}`}>{status(r)}</td>
                  </tr>))}</tbody>
              </table>
            </div>
          )}
        </div>
        {fails.length > 0 && (
          <ul role="alert" className="mx-5 mb-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 space-y-0.5">
            <li className="font-medium">{fails.length}줄 못 넣음 — 나머지는 들어갔습니다</li>
            {fails.map((f) => <li key={f}>{f}</li>)}
          </ul>
        )}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
          <button onClick={() => { setRows([]); onClose(); }} className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50">취소</button>
          <button onClick={run} disabled={ok.length === 0 || busy} className="px-5 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">
            {busy ? '등록 중...' : `${ok.length}곳 등록`}
          </button>
        </div>
      </div>
    </div>
  );
};
